import { Map, List, fromJS } from "immutable";
import groupBy from "lodash/groupBy";
import { combineReducers } from "redux";
import * as actionTypes from "~/store/action_types";
import {
  ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR,
  ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING,
  DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS,
  FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS,
} from "~/store/action_types";

const initialParentState = List();
const updateParents = (state = initialParentState, action) => {
  const parentId = parseInt(action.is_parent ? action.dependency_id : action.projectTemplateTaskId);
  switch (action.type) {
    case actionTypes.FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      const { response = {} } = action;
      const { result = {} } = response;
      const { parents = [] } = result;
      return List(parents);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING: {
      return state.push(parentId);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR: {
      return state.filter((id) => id !== parentId);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      if (action.is_parent) {
        return state.filter((id) => id !== action.dependency_id);
      }
      return state;
    }
    default:
      return state;
  }
};

const initialParentsState = Map();
const parents = (state = initialParentsState, action) => {
  const child = action.is_parent ? action.projectTemplateTaskId : action.dependency_id;
  switch (action.type) {
    case actionTypes.FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      const { response = {} } = action;
      const { result = {} } = response;
      const { parents = [] } = result;
      if (parents.length > 0) {
        return state.set(
          `${action.projectTemplateTaskId}`,
          updateParents(state.get(`${action.projectTemplateTaskId}`), action)
        );
      }
      return state;
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING: {
      return state.set(`${child}`, updateParents(state.get(`${child}`), action));
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR: {
      const parentsList = updateParents(state.get(`${child}`), action);
      if (parentsList.size > 0) {
        return state.set(`${child}`, parentsList);
      }
      return state.delete(`${child}`);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_ELEMENT_LOADING: {
      const { id = 0 } = action;
      if (id) {
        state = state.delete(`${id}`);
      }
      return state;
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      return state.set(
        `${action.projectTemplateTaskId}`,
        updateParents(state.get(`${action.projectTemplateTaskId}`), action)
      );
    }
    default:
      return state;
  }
};

const initialChildState = List();
const updateChildren = (state = initialChildState, action) => {
  const childId = parseInt(action.is_parent ? action.projectTemplateTaskId : action.dependency_id);
  switch (action.type) {
    case actionTypes.FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      const { response = {} } = action;
      const { result = {} } = response;
      const { children = [] } = result;
      return List(children);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING: {
      return state.push(childId);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR: {
      return state.filter((id) => id !== childId);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      if (!action.is_parent) {
        return state.filter((id) => id !== action.dependency_id);
      }
      return state;
    }
    default:
      return state;
  }
};

const initialChildrenState = Map();
const children = (state = initialChildrenState, action) => {
  const parent = action.is_parent ? action.dependency_id : action.projectTemplateTaskId;
  switch (action.type) {
    case actionTypes.FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      const { response = {} } = action;
      const { result = {} } = response;
      const { children = [] } = result;
      if (children.length > 0) {
        return state.set(
          `${action.projectTemplateTaskId}`,
          updateChildren(state.get(`${action.projectTemplateTaskId}`), action)
        );
      }
      return state;
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING: {
      return state.set(`${parent}`, updateChildren(state.get(`${parent}`), action));
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR: {
      const childrenList = updateChildren(state.get(`${parent}`), action);
      if (childrenList.size > 0) {
        return state.set(`${parent}`, childrenList);
      }
      return state.delete(`${parent}`);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_ELEMENT_LOADING: {
      const { id = 0 } = action;
      if (id) {
        state = state.delete(`${id}`);
      }
      return state;
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      const newChildren = updateChildren(state.get(`${parent}`), action);
      return newChildren.size > 0 ? state.set(`${parent}`, newChildren) : state;
    }
    default:
      return state;
  }
};

const initialSuggestionState = List();
const updateSuggestion = (state = initialSuggestionState, action) => {
  switch (action.type) {
    case actionTypes.FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS: {
      const { response = {} } = action;
      const { result = [] } = response;
      return List(result);
    }
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
      return state.filter((id) => id !== action.dependency_id);
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS:
      return state.push(action.dependency_id);
    default:
      return state;
  }
};

const initialSuggestionsState = Map();
const suggestions = (state = initialSuggestionsState, action) => {
  if (!action.type) return state;
  switch (action.type) {
    case actionTypes.ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
      return state.set(
        `${action.projectTemplateTaskId}`,
        updateSuggestion(state.get(`${action.projectTemplateTaskId}`), action)
      );
    case actionTypes.DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS:
      return state.set(
        `${action.projectTemplateTaskId}`,
        updateSuggestion(state.get(`${action.projectTemplateTaskId}`), action)
      );
    case actionTypes.FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS: {
      const { response = {} } = action;
      const { result = [] } = response;
      if (result.length > 0) {
        return state.set(
          `${action.projectTemplateTaskId}`,
          updateSuggestion(state.get(`${action.projectTemplateTaskId}`), action)
        );
      }
      return state.delete(`${action.projectTemplateTaskId}`);
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_ELEMENT_LOADING: {
      const { id } = action;
      return state.map((suggestions) => {
        return suggestions.filter((suggestion) => suggestion !== id);
      });
    }
    default:
      return state;
  }
};

const initialGroupedSuggestionState = Map();
const updateSuggestionsByTaskList = (state = initialGroupedSuggestionState, action) => {
  switch (action.type) {
    case ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR: {
      const { taskListId, dependency_id } = action;
      state[taskListId] = state[taskListId] ? state[taskListId].push(dependency_id) : List([dependency_id]);
      return state;
    }
    case DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS: {
      const { taskListId, dependency_id } = action;
      const newSuggestionsByTaskListId = state.get(`${taskListId}`)
        ? state.get(`${taskListId}`).push(dependency_id)
        : List([dependency_id]);

      return state.set(`${taskListId}`, newSuggestionsByTaskListId);
    }

    case ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
      state = state.map((tasks) => {
        // should remove selected task id from suggestions
        return tasks.filter((taskId) => taskId !== action.dependency_id);
      });

      return state.filter((tasks) => tasks.size);

    case FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS: {
      const { response = {} } = action;
      const { entities = {} } = response;
      const { projectTemplateTasks = {} } = entities;
      let tasksGroupedByTaskListId = groupBy(projectTemplateTasks, `task_list_id`);

      Object.keys(tasksGroupedByTaskListId).forEach((taskListId) => {
        tasksGroupedByTaskListId[taskListId] = tasksGroupedByTaskListId[taskListId].map((task) => task.id);
      });

      return fromJS(tasksGroupedByTaskListId);
    }
    default:
      return state;
  }
};

const initialGroupedSuggestionsState = Map();
const groupedSuggestions = (state = initialGroupedSuggestionsState, action) => {
  if (!action.type) return state;
  switch (action.type) {
    case ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_ERROR:
    case ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_LOADING:
    case DELETE_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS:
    case FETCH_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUGGESTIONS_SUCCESS: {
      return state.set(
        `${action.projectTemplateTaskId}`,
        updateSuggestionsByTaskList(state.get(`${action.projectTemplateTaskId}`), action)
      );
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_ELEMENT_LOADING: {
      const { id } = action;
      return state.map((groupedSuggestions) => {
        return groupedSuggestions.filter((suggestion) => {
          return suggestion !== id;
        });
      });
    }
    default:
      return state;
  }
};

export default combineReducers({
  parents,
  children,
  suggestions,
  groupedSuggestions,
});
